import {
  CalendarDaysIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  PlayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  AssignmentKindType,
  AssignmentRecordingItem,
  AssignmentStatusByStudentItem,
  AssignmentStatusDetailByStudentItem,
} from '../../../../constants/types/lms/assignment/type';
import {
  formatToKstDate,
  getErrorMessages,
} from '../../../../api/utils/util-func';
import TextWinds from '../../../common/TextWinds';
import { ElementType, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../../store/hook';
import { openAlert, openConfirm } from '../../../../store/features/modalSlice';
import {
  openTodoView,
  resolveJebsplayerUrl,
} from '../../../../util/openContentsWindow';
import {
  deleteAssignmentByStudent,
  getAssignmentRecordingList,
} from '../../../../api/utils/lms/assignment/api';
import RecordingAnswerPanel from './RecordingPanel';
import {
  getProgressColorClassNames,
  getProgressRate,
  getStatusClassName,
  getStatusLabel,
  getTestScoreColorClassName,
} from '../../../../pages/lms/assignment-mgmt/AssignmentStatus';
import { useParams } from 'react-router-dom';
import { Checkbox } from 'antd';

interface AssignmentStatusByStudentCardProps {
  selectedStudent: AssignmentStatusByStudentItem;
  assignment: AssignmentStatusDetailByStudentItem;
  checked: boolean;
  onCheckedChange: (assignmentId: number, checked: boolean) => void;
  fetchList: () => Promise<void>;
}

const ASSIGNMENT_KIND_META_MAP: Record<
  AssignmentKindType,
  {
    label: string;
    className: string;
    icon: ElementType;
  }
> = {
  [AssignmentKindType.TEST]: {
    label: '테스트',
    className: 'bg-blue-50 text-[#0B8ED0]',
    icon: DocumentTextIcon,
  },
  [AssignmentKindType.ASSIGNMENT]: {
    label: '과제',
    className: 'bg-green-50 text-green-600',
    icon: PencilSquareIcon,
  },
};

/**
 * 개편 LMS (교사) - 과제 관리 > 과제 현황 페이지 > 학생별 과제 현황 > 학생별 과제 카드
 */
const AssignmentStatusByStudentCard = ({
  selectedStudent,
  assignment,
  checked,
  onCheckedChange,
  fetchList,
}: AssignmentStatusByStudentCardProps) => {
  const { classId } = useParams();

  const dispatch = useAppDispatch();

  const handleDelete = async (todoId: number) => {
    dispatch(
      openConfirm({
        message: (
          <div className="flex flex-col md:my-3">
            <div className="text-base">삭제하시면 되돌릴 수 없습니다.</div>

            <div className="text-base">정말 삭제하시겠습니까?</div>
          </div>
        ),
        confirmText: '삭제하기',
        cancelText: '닫기',
        onConfirm: async () => {
          try {
            await deleteAssignmentByStudent({
              classId,
              todoId,
            });

            await fetchList();
          } catch (error) {
            dispatch(openAlert({ message: getErrorMessages(error) }));
          }
        },
      }),
    );
  };

  const setTodoView = useState<Window>(undefined)[1];

  const progressRate = getProgressRate(assignment.progress);

  const isCompleted =
    assignment.progressState === 'COMPLETED' || progressRate >= 100;

  const {
    barClassName: progressBarColorClassName,
    textClassName: progressTextColorClassName,
  } = getProgressColorClassNames({
    progressRate,
    isCompleted,
  });

  const assignmentKindMeta = ASSIGNMENT_KIND_META_MAP[assignment.todoKind] ?? {
    label: '과제',
    className: 'bg-gray1 text-gray7',
    icon: DocumentTextIcon,
  };

  const AssignmentKindIcon = assignmentKindMeta.icon;

  const testScore = assignment.testScore ?? 0;

  const testItems = assignment.testItems ?? [];

  const totalQuestionCount = testItems.length;
  const correctQuestionCount = testItems.filter((item) => item.correct).length;

  const testResultText = `(${correctQuestionCount}/${totalQuestionCount})`;

  const hasTestItems =
    assignment.todoKind === AssignmentKindType.TEST &&
    Array.isArray(assignment.testItems) &&
    assignment.testItems.length > 0;

  const handleShowResult = () => {
    if (!assignment.playerLink) {
      dispatch(
        openAlert({
          message: '과제 링크가 없습니다. 관리자에게 문의해주세요.',
        }),
      );
      return;
    }

    openTodoView(
      { url: resolveJebsplayerUrl(assignment.playerLink) },
      setTodoView,
    );
  };

  const [recordingList, setRecordingList] = useState<AssignmentRecordingItem[]>(
    [],
  );

  const [isRecordingPanelOpened, setIsRecordingPanelOpened] = useState(false);

  const fetchAssignmentRecordingList = async () => {
    try {
      const response = await getAssignmentRecordingList({
        studentId: selectedStudent.id,
        todoId: assignment.id,
      });

      setRecordingList(response.items ?? []);
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    }
  };

  useEffect(() => {
    if (assignment.recordingCount <= 0) return;

    fetchAssignmentRecordingList();
  }, [assignment]);

  const recordingListByContentSeq = useMemo(() => {
    const latestRecordingMapByContentSeq = recordingList.reduce<
      Record<number, (typeof recordingList)[number]>
    >((acc, recording) => {
      if (recording.contentSeq == null) {
        return acc;
      }

      const prevRecording = acc[recording.contentSeq];

      if (!prevRecording) {
        acc[recording.contentSeq] = recording;
        return acc;
      }

      const prevTime = new Date(prevRecording.createdAt).getTime();
      const currentTime = new Date(recording.createdAt).getTime();

      if (currentTime > prevTime) {
        acc[recording.contentSeq] = recording;
      }

      return acc;
    }, {});

    return Object.entries(latestRecordingMapByContentSeq)
      .map(([contentSeq, recording]) => ({
        contentSeq: Number(contentSeq),
        recording,
      }))
      .sort((a, b) => a.contentSeq - b.contentSeq);
  }, [recordingList]);

  const submittedRecordingCount = recordingListByContentSeq.length;
  const hasRecordingItems = submittedRecordingCount > 0;

  const canDelete = assignment.progressState === 'NOT_STARTED';

  return (
    <article className="overflow-hidden rounded-xl border border-gray3 bg-white shadow-sm">
      <div className="flex flex-col gap-3 px-4 py-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex min-w-0 gap-3">
            <Checkbox
              checked={checked}
              disabled={!canDelete}
              onChange={(event) =>
                onCheckedChange(assignment.id, event.target.checked)
              }
              className="mt-1 shrink-0"
            />

            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-purple1 px-2.5 py-1 text-sm font-bold text-purple5">
                  {assignment.book}
                </span>

                <TextWinds type="content_body3" color="gray8">
                  {assignment.lesson}
                </TextWinds>

                <TextWinds
                  type="content_body2"
                  color="gray9"
                  className="font-bold"
                >
                  {assignment.title}
                </TextWinds>

                <span
                  className={[
                    'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold',
                    assignmentKindMeta.className,
                  ].join(' ')}
                >
                  <AssignmentKindIcon className="h-3.5 w-3.5" />
                  {assignmentKindMeta.label}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <div className="flex items-center gap-1.5">
                  <CalendarDaysIcon className="h-4 w-4 text-gray6" />

                  <TextWinds type="caption1" color="gray7">
                    내준 날짜
                  </TextWinds>

                  <TextWinds
                    type="caption1"
                    color="gray9"
                    className="font-bold"
                  >
                    {formatToKstDate(assignment.fromDate, 'YYYY.MM.DD')}
                  </TextWinds>
                </div>

                <div className="flex items-center gap-1.5">
                  <CalendarDaysIcon className="h-4 w-4 text-gray6" />

                  <TextWinds type="caption1" color="gray7">
                    마감일
                  </TextWinds>

                  <TextWinds
                    type="caption1"
                    color="gray9"
                    className="font-bold"
                  >
                    {formatToKstDate(assignment.toDate, 'YYYY.MM.DD')}
                  </TextWinds>
                </div>
              </div>
            </div>
          </div>

          {canDelete && (
            <button
              type="button"
              className="flex shrink-0 items-center gap-1 self-end rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-bold text-red-500 transition-colors hover:border-red-400 hover:bg-red-50 xl:self-start"
              onClick={() => handleDelete(assignment.id)}
            >
              <TrashIcon className="h-4 w-4" />
              삭제
            </button>
          )}
        </div>

        <div className="mt-1 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray1">
            <div
              className={[
                'h-full rounded-full transition-all duration-300',
                progressBarColorClassName,
              ].join(' ')}
              style={{ width: `${progressRate}%` }}
            />
          </div>

          <TextWinds
            type="caption1"
            className={[
              'w-[40px] shrink-0 text-right font-bold',
              progressTextColorClassName,
            ].join(' ')}
          >
            {progressRate}%
          </TextWinds>

          <div
            className={[
              'shrink-0 rounded-full px-3 py-1.5',
              getStatusClassName(assignment.progressState),
            ].join(' ')}
          >
            <TextWinds
              type="caption1"
              className={[
                'flex items-center gap-1 font-bold',
                getStatusClassName(assignment.progressState),
              ].join(' ')}
            >
              {getStatusLabel(assignment.progressState)}
            </TextWinds>
          </div>
        </div>
      </div>

      {assignment.completedDate && (
        <div className="border-t border-gray2 px-4 py-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                <TextWinds type="caption1" color="gray7">
                  학습 회차{' '}
                  <span className="font-bold text-gray9">
                    {assignment.completedCount ?? 0}
                  </span>
                  회
                </TextWinds>

                <span className="text-xs text-gray5">·</span>

                <TextWinds type="caption1" color="gray7">
                  완료일{' '}
                  <span className="font-bold text-gray9">
                    {formatToKstDate(
                      assignment.completedDate,
                      'YYYY.MM.DD HH:mm',
                    )}
                  </span>
                </TextWinds>
              </div>
            </div>

            {assignment.todoKind === AssignmentKindType.TEST && (
              <div className="flex shrink-0 items-center justify-end gap-3">
                <TextWinds
                  type="content_body2"
                  className={[
                    'font-bold',
                    getTestScoreColorClassName(testScore),
                  ].join(' ')}
                >
                  {testScore}점
                </TextWinds>

                <TextWinds type="content_body4">{testResultText}</TextWinds>

                <button
                  type="button"
                  className={[
                    'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                    hasTestItems
                      ? 'border-[#0B8ED0] bg-white text-[#0B8ED0] hover:bg-blue-50'
                      : 'cursor-not-allowed border-gray3 bg-gray1 text-gray5',
                  ].join(' ')}
                  disabled={!hasTestItems}
                  onClick={handleShowResult}
                >
                  <DocumentCheckIcon className="h-4 w-4" />

                  <span>결과 보기</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {hasRecordingItems && (
        <div className="border-t border-gray2 px-4 py-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0"></div>

            <div className="flex shrink-0 flex-nowrap items-center gap-2">
              <button
                type="button"
                className={[
                  'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                  isRecordingPanelOpened
                    ? 'border-[#0B8ED0] bg-blue-100 text-[#0B8ED0]'
                    : 'border-[#0B8ED0] bg-white text-[#0B8ED0] hover:bg-blue-50',
                ].join(' ')}
                disabled={!hasRecordingItems}
                onClick={() => setIsRecordingPanelOpened((prev) => !prev)}
              >
                <PlayIcon className="h-4 w-4" />
                <span>녹음 듣기</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {isRecordingPanelOpened && hasRecordingItems && (
        <RecordingAnswerPanel
          studentName={selectedStudent.name}
          assignmentTitle={assignment.title}
          recordingListByContentSeq={recordingListByContentSeq}
        />
      )}
    </article>
  );
};

export default AssignmentStatusByStudentCard;
